import { getContrast, getLuminance } from 'polished';

import { COLOR_TOKENS } from 'constants/quartz/color_tokens';

/**
 * The WCAG 2.1 AA contrast ratio for normal body text.
 *
 * @type {number}
 */
export const MIN_CONTRAST_RATIO = 4.5;

/**
 * Check whether two colors meet a contrast ratio.
 * Unparsable colors are treated as passing so that a partner value we cannot
 * read never blocks rendering.
 *
 * @method meetsContrast
 * @param  {string}      foreground        The foreground color
 * @param  {string}      background        The background color
 * @param  {number}      [ratio=4.5]       The minimum ratio
 * @return {boolean}                       Whether the pair meets the ratio
 */
export const meetsContrast = (foreground, background, ratio = MIN_CONTRAST_RATIO) => {
    try {
        return getContrast(foreground, background) >= ratio;
    } catch (error) {
        return true;
    }
};

/**
 * Pick the readable Quartz text color for a background.
 *
 * @method getReadableTextColor
 * @param  {string}             background The background color
 * @return {string}                        The readable text color
 */
export const getReadableTextColor = background => {
    try {
        return getLuminance(background) > 0.5
            ? COLOR_TOKENS.text.primary
            : COLOR_TOKENS.text.inverse;
    } catch (error) {
        return COLOR_TOKENS.text.primary;
    }
};

/**
 * Resolve the text color to render on top of a partner background.
 * The partner's own text color is kept whenever it is readable, and only
 * degrades to the readable Quartz text color when it fails the contrast ratio.
 *
 * @method resolveTextColorOn
 * @param  {string}           background  The partner background color
 * @param  {string}           textColor   The configured text color
 * @param  {number}           [ratio=4.5] The minimum contrast ratio
 * @return {string}                       The text color to render
 */
export const resolveTextColorOn = (background, textColor, ratio = MIN_CONTRAST_RATIO) => {
    if (!background || !textColor) {
        return textColor;
    }

    return meetsContrast(textColor, background, ratio)
        ? textColor
        : getReadableTextColor(background);
};
